const platform = process.env.REACT_APP_PLATFORM;
const isTH = platform === 'tickethoss';
// Pit Pay is the default platform (no REACT_APP_PLATFORM) as well as the
// explicit one; Ticket Hoss and Kart Pass are the others.
const isPitPay = !platform || platform === 'pitpay';

export const getNavSections = (role, capabilities, hasRegistrationEvents) => {
  switch (role) {
    case 'admin':
      return getAdminSections();
    case 'track':
    case 'employee':
      return getPromoterSections(role, capabilities, hasRegistrationEvents);
    default:
      return [];
  }
};

function getAdminSections() {
  const sections = [
    {
      id: 'events',
      icon: 'Events',
      label: 'Events',
      type: 'section',
      link: '/admin/events/upcoming',
      shown: true,
      panelItems: [
        { label: 'Upcoming', link: '/admin/events/upcoming' },
        { label: 'Past Events', link: '/admin/events/past' },
        { label: 'Drafts', link: '/admin/events/drafts' },
        !isTH && {
          label: 'Registrations',
          link: '/admin/registrations',
        },
      ].filter(Boolean),
    },
    {
      id: 'users',
      icon: 'users',
      label: 'Users',
      type: 'section',
      link: '/admin/users',
      shown: true,
      panelItems: [
        { label: 'All Users', link: '/admin/users' },
        !isTH && { label: 'Temporary', link: '/admin/temporaryUsers' },
        !isTH && { label: 'Approved', link: '/admin/approvedUsers' },
      ].filter(Boolean),
    },
    {
      id: 'accounts',
      icon: 'Accounts',
      label: 'Accounts',
      type: 'direct',
      link: '/admin/accounts',
      shown: true,
    },
    {
      id: 'tracks',
      icon: 'Tracks2',
      label: 'Tracks',
      type: 'section',
      link: '/admin/tracks',
      shown: true,
      panelItems: [
        { label: 'All Tracks', link: '/admin/tracks' },
        { label: 'Track Types', link: '/admin/track-types' },
      ],
    },
    {
      id: 'series',
      icon: 'Series',
      label: 'Series',
      type: 'section',
      link: '/admin/series',
      shown: true,
      panelItems: [
        { label: 'All Series', link: '/admin/series' },
        { label: 'Series Types', link: '/admin/series-types' },
      ],
    },
    {
      id: 'messages',
      icon: 'Message-Center',
      label: 'Msg Ctr',
      type: 'section',
      link: '/admin/notifications',
      shown: true,
      panelItems: [{ label: 'All Messages', link: '/admin/notifications' }],
    },
    {
      id: 'financial',
      icon: 'Transactions',
      label: 'Financial',
      type: 'section',
      link: '/admin/transactions',
      shown: true,
      panelItems: [
        { label: 'Transactions', link: '/admin/transactions' },
        { label: 'Revenue', link: '/admin/revenue' },
        { label: 'Payouts', link: '/admin/payouts' },
        { label: 'Discounts', link: '/admin/promos' },
        {
          label: 'Credits',
          type: 'expandable',
          children: [
            { label: 'Open Credits', link: '/admin/openCredits' },
            { label: 'Redeemed Credits', link: '/admin/redeemedCredits' },
            { label: 'Expired Credits', link: '/admin/expiredCredits' },
          ],
        },
      ],
    },
    {
      id: 'settings',
      icon: 'Dashboard',
      label: 'Settings',
      type: 'section',
      link: '/admin/categories',
      shown: true,
      panelItems: [
        { label: 'Learning Hub', link: '/admin/learning-hub' },
        { label: 'Learning Hub Manager', link: '/admin/learning-hub-admin' },
        { label: 'Reports', link: '/admin/reports' },
        !isTH && { label: 'Waivers', link: '/admin/waivers' },
        { label: 'Forms', link: '/admin/forms' },
        // W-9 collection (w9-plan §6.1, D10): Pit Pay only in v1.
        isPitPay && { label: 'W-9s', link: '/admin/w9' },
        {
          label: 'Categories',
          type: 'expandable',
          children: [
            { label: 'Ticket Categories', link: '/admin/categories' },
            { label: 'Info Tab Categories', link: '/admin/infos' },
          ],
        },
      ].filter(Boolean),
    },
  ];

  return sections;
}

function getPromoterSections(role, capabilities, hasRegistrationEvents) {
  const prefix = `/admin-${role}`;

  // Per-item capability checks for Financial
  const hasTransactions = capabilities?.transactions ?? false;
  const hasCredits = capabilities?.credits ?? false;
  const hasPromos = capabilities?.promos ?? false;
  const hasStripe = capabilities?.stripe ?? false;
  const hasAnyFinancial =
    hasTransactions || hasCredits || hasPromos || hasStripe;

  const sections = [
    {
      id: 'home',
      icon: 'Home',
      label: 'Home',
      type: 'direct',
      link: `${prefix}/home`,
      shown: true,
    },
    {
      id: 'events',
      icon: 'Events',
      label: 'Events',
      type: 'section',
      link: `${prefix}/events/upcoming`,
      shown: capabilities?.events ?? false,
      panelItems: [
        { label: 'Upcoming', link: `${prefix}/events/upcoming` },
        { label: 'Past Events', link: `${prefix}/events/past` },
        !isTH &&
          hasRegistrationEvents && {
            label: 'Registrations',
            link: `${prefix}/registrations`,
          },
      ].filter(Boolean),
    },
    {
      id: 'weather',
      icon: 'Weather',
      label: 'Weather',
      type: 'direct',
      link: `${prefix}/weather`,
      shown: true,
    },
    {
      id: 'messages',
      icon: 'Message-Center',
      label: 'Messages',
      type: 'section',
      link: `${prefix}/notifications`,
      shown: capabilities?.messages ?? false,
      panelItems: [{ label: 'All Messages', link: `${prefix}/notifications` }],
    },
    {
      id: 'guests',
      icon: 'Guest-List',
      label: 'Guests',
      type: 'section',
      link: `${prefix}/guest-lists`,
      shown: capabilities?.guests ?? false,
      panelItems: [
        { label: 'Event Guest Lists', link: `${prefix}/guest-lists` },
        { label: 'All Guests', link: `${prefix}/guests` },
      ],
    },
    {
      id: 'season',
      icon: isTH ? 'Season-Tickets' : 'Members',
      label: isTH ? 'Season' : 'Annuals',
      type: 'section',
      link: `${prefix}/annualMembers`,
      shown: capabilities?.members ?? true,
      panelItems: [
        !isTH && {
          label: 'Members',
          link: `${prefix}/annualMembers`,
        },
        {
          label: isTH ? 'Ticket Holders' : 'Annuals',
          link: `${prefix}/annualUsers`,
        },
      ].filter(Boolean),
    },
    {
      id: 'customers',
      icon: 'Customers',
      label: 'Customers',
      type: 'direct',
      link: `${prefix}/users`,
      shown: capabilities?.customers ?? false,
    },
    {
      id: 'staff',
      icon: 'Staff',
      label: 'Staff',
      type: 'direct',
      link: `${prefix}/employees`,
      shown: capabilities?.staff ?? false,
    },
    {
      id: 'financial',
      icon: 'Transactions',
      label: 'Financial',
      type: 'section',
      link: `${prefix}/transactions`,
      shown: hasAnyFinancial,
      panelItems: [
        {
          label: 'Event Transactions',
          link: `${prefix}/transactions`,
          shown: hasTransactions,
        },
        {
          label: isTH ? 'Season Ticket Sales' : 'Full Season',
          link: `${prefix}/annualPurchases`,
          shown: hasTransactions,
        },
        {
          label: 'Discounts',
          link: `${prefix}/promos`,
          shown: hasPromos,
        },
        {
          label: 'Stripe',
          type: 'stripe',
          shown: hasStripe,
        },
        {
          label: 'Credits',
          type: 'expandable',
          shown: hasCredits,
          children: [
            { label: 'Open Credits', link: `${prefix}/openCredits` },
            { label: 'Redeemed Credits', link: `${prefix}/redeemedCredits` },
            { label: 'Expired Credits', link: `${prefix}/expiredCredits` },
          ],
        },
      ].filter(Boolean),
    },
    {
      id: 'help',
      icon: 'help',
      label: 'Help',
      type: 'section',
      link: null,
      shown: true,
      panelTitle: 'Help & Resources',
      panelItems: [
        {
          label: 'Learning Hub',
          link: `${prefix}/learning-hub`,
        },
        {
          label: 'Help Center',
          type: 'external',
          link: 'https://promoters.pitpay.com/help',
        },
        {
          label: 'Contact Us',
          link: `${prefix}/contact`,
        },
      ],
    },
  ];

  return sections;
}
